import * as Select from '@radix-ui/react-select';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import { DropdownItemLabel } from '~/chords/DropdownSingleSelect/components/DropdownItemLabel';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';

interface DropdownItemProps extends RadixUiProps {
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownItem({
  option,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemProps) {
  const handleOptionMouseUp = useCallback(() => {
    if (!option.disabled) {
      onSelectOption?.(option);
    }
  }, [onSelectOption, option]);

  const handleOptionKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // Select the option when the Enter or Space key is pressed.
      if (!option.disabled && ['Enter', ' '].includes(event.key)) {
        onSelectOption?.(option);
      }
    },
    [onSelectOption, option],
  );

  if (!option.isVisible) {
    return null;
  }

  let OptionLabel = OptionTemplate || DropdownItemLabel;

  return (
    <Select.Item
      {...radix.selectItem}
      value={option.value}
      disabled={option.disabled}
      className={cn(
        'flex min-h-[36px] items-center gap-xs px-md transition-colors duration-300',
        {
          'text-default': !option.disabled,
          'hover:bg-emphasis-strong': !option.disabled,
          'focus:outline-none focus:bg-emphasis-strong': !option.disabled,
        },
        'data-[state=checked]:bg-primary-default',
        'data-[state=checked]:focus:bg-secondary-default',
        option.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        radix.selectItem?.className,
      )}
      onMouseUp={handleOptionMouseUp}
      onKeyUp={handleOptionKeyUp}
      asChild
      aria-selected={selectedOption?.value === option.value}
    >
      <div>
        {showCheckIcon ? (
          <div className="-ml-xs w-md">
            <Select.ItemIndicator>
              <Icon type="check" size="sm" />
            </Select.ItemIndicator>
          </div>
        ) : null}
        <Select.ItemText asChild>
          <div className="min-w-0 flex-1">
            <OptionLabel option={option} searchQuery={searchQuery} />
          </div>
        </Select.ItemText>
      </div>
    </Select.Item>
  );
}

DropdownItem.displayName = 'DropdownItem';
